// Every navigation decision for room connections is made here, so route changes
// and failures go through one place. A room close navigates; a message for
// another room, such as one that arrives after the user navigates away, only
// runs the cleanup callbacks and leaves the user where they went.

import React, {
  createContext,
  useMemo,
  useState,
  useEffect,
  useCallback,
  useRef,
} from 'react';
import { useNavigate } from 'react-router';
// Note: Import hooks directly (not from "../hooks") to avoid cycles.
import { useRoute } from '../hooks/useRoute';
import { useSystemDeviceDetection } from '../hooks/useSystemDetectDevice';
import { useSystemWindowSize } from '../hooks/useSystemWindowSize';
import {
  RoomSocketClient,
  RoomSocketClientEvents,
} from '../transport/websocket/roomSocketClient';
import { RoomConnectionManager } from '../transport/websocket/roomConnectionManager';
import {
  PlatformEvent,
  PlatformCommand,
  PlatformQuery,
  ClientInfo,
  ConnectionState,
} from '@airhead-games/shared/types';
import { createLogger, delay } from '@airhead-games/shared/utilities';

const logger = createLogger('RoomSocketContext');

export interface RoomSocketContextType {
  wsClient: RoomSocketClient;
  connectionManager: RoomConnectionManager;
  connectionState: ConnectionState;
  closeReason: string | undefined;
  isManuallyClosed: boolean;
  isConnectionReady: boolean;
  waitForConnectionReady: () => Promise<void>;
  retryConnection: () => void;
  clearCloseReason: () => void;

  // Transport operations
  sendCommand: (command: PlatformCommand) => void;
  sendQuery: (query: PlatformQuery) => void;

  // Hierarchical event forwarding (for child contexts)
  registerRoomEventHandler: (
    handler: (payload: { events: PlatformEvent[] }) => void
  ) => void;

  // Navigation cleanup registration
  registerCleanupCallback: (callback: () => void) => () => void;
}

export const RoomSocketContext = createContext<RoomSocketContextType | null>(
  null
);

export const RoomSocketProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const navigate = useNavigate();
  const { roomCode: urlRoomCode, isPlayingRoute, isHostRoute } = useRoute();
  const deviceInfo = useSystemDeviceDetection();
  const { width, height } = useSystemWindowSize();
  const [connectionState, setConnectionState] = useState<ConnectionState>(
    ConnectionState.DISCONNECTED
  );
  const [closeReason, setCloseReason] = useState<string | undefined>(undefined);
  const [isConnectionReady, setIsConnectionReady] = useState(false);

  const connectedRoomCodeRef = useRef<string | null>(null); // Track current connected room for validation
  const autoConnectInProgressRef = useRef<boolean>(false); // Track auto-connect progress to prevent duplicates
  const connectionReadyPromiseRef = useRef<Promise<void> | null>(null); // Track waitForConnectionReady promise to prevent duplicates
  const clientInfoRef = useRef<ClientInfo | null>(null); // Avoid recreating WebSocket clients on dimension changes

  // Callback refs for hierarchical event forwarding to child contexts
  const roomEventCallbackRef = useRef<
    ((payload: { events: PlatformEvent[] }) => void) | null
  >(null);

  // Update client info without recreating WebSocket clients
  const updateClientInfo = useCallback(() => {
    clientInfoRef.current = {
      device: deviceInfo,
      screen: {
        width,
        height,
      },
      audio: {
        supportsWebAudio:
          'AudioContext' in window || 'webkitAudioContext' in window,
      },
      clientTimestamp: Date.now(),
    };
  }, [deviceInfo, width, height]);

  // Initialize client info once
  if (!clientInfoRef.current) {
    updateClientInfo();
  }

  // Update client info when dependencies change without recreating client
  useEffect(() => {
    updateClientInfo();
  }, [updateClientInfo]);

  // Centralized role-based navigation helper
  const navigateBasedOnRole = useCallback(
    (reason: string) => {
      if (!isPlayingRoute && !isHostRoute) {
        logger.debug(`Skipping navigation (${reason}): not on a room route`);
        return;
      }
      logger.debug(`Navigating based on role: ${reason}`);
      if (isPlayingRoute) {
        logger.debug('Player: navigating to join page...');
        navigate('/join');
      } else {
        logger.debug('Host: navigating to game library...');
        navigate('/library');
      }
    },
    [isPlayingRoute, isHostRoute, navigate]
  );

  // Create a single instance of the client and manager for the lifetime of the provider
  // Only create once to prevent connection leaks
  const contextValue = useMemo(() => {
    const client = new RoomSocketClient({ clientInfo: clientInfoRef.current! });
    const manager = new RoomConnectionManager(client);
    return { wsClient: client, connectionManager: manager };
  }, []); // No dependencies - create only once

  // Cleanup WebSocket connections on unmount
  useEffect(() => {
    return () => {
      logger.debug('RoomSocketProvider unmounting, cleaning up connections...');
      contextValue.connectionManager.disconnect();
      autoConnectInProgressRef.current = false; // Reset auto-connect progress
      setIsConnectionReady(false); // Reset connection ready state
      setConnectionState(ConnectionState.DISCONNECTED); // Reset connection state
    };
  }, [contextValue.connectionManager]);

  // Cleanup callbacks for room mismatch handling
  const cleanupCallbacks = useMemo(() => new Set<() => void>(), []);

  // Subscribe to connection state changes and handle connection errors
  useEffect(() => {
    const { wsClient } = contextValue;
    const handler = (payload: {
      state: ConnectionState;
      code?: number;
      reason?: string;
    }) => {
      setConnectionState(payload.state);
      setCloseReason(payload.reason);

      // Reset auto-connect flag on final failure states
      // Self-healing behavior for network issues and so users don't get stuck on room routes
      if (
        payload.state === ConnectionState.FAILED ||
        payload.state === ConnectionState.RECONNECT_FAILED ||
        payload.state === ConnectionState.TIMEOUT
      ) {
        autoConnectInProgressRef.current = false;
      }

      // Reset ready state when not connected
      if (payload.state !== ConnectionState.CONNECTED) {
        setIsConnectionReady(false);
      }
    };
    wsClient.on('connectionState', handler);
    return () => wsClient.off('connectionState', handler);
  }, [contextValue, navigateBasedOnRole]);

  // Unified room management utilities
  const roomManager = useMemo(() => {
    const triggerNavigationCleanup = (reason: string) => {
      logger.debug(`Triggering navigation cleanup: ${reason}`);
      cleanupCallbacks.forEach((callback) => callback());
    };

    const validateRoomMessage = (roomCode: string, messageType: string) => {
      if (urlRoomCode && roomCode !== urlRoomCode) {
        logger.warn(
          `Dropping ${messageType} for room ${roomCode} (expected ${urlRoomCode})`
        );
        triggerNavigationCleanup(`room mismatch for ${messageType}`);
        return false;
      }
      return true;
    };

    const handleRoomCodeChange = (currentConnectedRoom: string | null) => {
      // If we're connected to a room but URL changed to different room or no room
      if (currentConnectedRoom && currentConnectedRoom !== urlRoomCode) {
        logger.debug(
          `Room mismatch detected: connected to ${currentConnectedRoom}, URL has ${urlRoomCode || 'no room'}`
        );

        // Trigger navigation cleanup (NO room closed event forwarding)
        triggerNavigationCleanup(
          `navigation from ${currentConnectedRoom} to ${urlRoomCode || 'none'}`
        );

        // Disconnect from current room
        contextValue.connectionManager.disconnect();
        connectedRoomCodeRef.current = null;
      }
    };

    return {
      validateRoomMessage,
      handleRoomCodeChange,
      triggerNavigationCleanup,
    };
  }, [urlRoomCode, cleanupCallbacks, contextValue.connectionManager]);

  // Message forwarding utility
  const createMessageForwarder = useCallback(
    <K extends keyof RoomSocketClientEvents>(
      messageType: K,
      callbackRef: React.RefObject<
        ((payload: RoomSocketClientEvents[K]) => void) | null
      >,
      validator?: (payload: RoomSocketClientEvents[K]) => boolean,
      onMessage?: (payload: RoomSocketClientEvents[K]) => void
    ) => {
      const { wsClient } = contextValue;

      const handler = (payload: RoomSocketClientEvents[K]) => {
        // Run validation if provided
        if (validator && !validator(payload)) {
          return;
        }

        // Run additional message processing if provided
        if (onMessage) {
          onMessage(payload);
        }

        // Forward to registered handler
        if (callbackRef.current) {
          callbackRef.current(payload);
        }
      };

      wsClient.on(messageType, handler);
      return () => wsClient.off(messageType, handler);
    },
    [contextValue]
  );

  // Event message forwarding with room validation and connection tracking
  useEffect(() => {
    return createMessageForwarder(
      'event',
      roomEventCallbackRef,
      // Validator - use first event in batch for validation
      (payload: { events: PlatformEvent[] }) => {
        const firstEvent = payload.events[0];
        return roomManager.validateRoomMessage(
          firstEvent.roomCode,
          `event batch (${payload.events.length} events)`
        );
      },
      // Additional processing - check any event for RoomClosed
      (payload: { events: PlatformEvent[] }) => {
        // Handle RoomClosed events at transport layer
        const hasRoomClosed = payload.events.some(
          (event) => event.type === 'RoomClosed'
        );

        if (hasRoomClosed) {
          logger.debug(
            'RoomClosed event received in batch, connection already closed by server'
          );
          connectedRoomCodeRef.current = null;
          navigateBasedOnRole('room closed');
        }
      }
    );
  }, [
    createMessageForwarder,
    roomManager,
    contextValue.connectionManager,
    navigateBasedOnRole,
  ]);

  // QUERY_ACK message handling for logging and debugging
  useEffect(() => {
    const handleQueryAck = (payload: RoomSocketClientEvents['query_ack']) => {
      const { queryType, status, metadata, error } = payload;
      if (queryType === 'STATE_SYNC') {
        if (metadata) {
          logger.info(
            `Query ACK received: ${status} - ${metadata.eventsToSend} events incoming (client: ${metadata.clientVersion}, server: ${metadata.serverVersion})`
          );
        }
        if (error) {
          logger.error(`Query error: ${error}`);
        }
      }
    };

    const unsubscribe = contextValue.wsClient.on('query_ack', handleQueryAck);
    return unsubscribe;
  }, [contextValue.wsClient]);

  // Monitor room code changes and trigger navigation cleanup if needed
  useEffect(() => {
    const currentConnectedRoom = connectedRoomCodeRef.current;
    // Only check for mismatches after urlRoomCode has been parsed
    if (urlRoomCode !== undefined) {
      roomManager.handleRoomCodeChange(currentConnectedRoom);
    }
  }, [roomManager, urlRoomCode]);

  // Connection status describes one room, so entering a different room must not
  // inherit the previous room's outcome: a stale terminal state blocks
  // auto-connect below, and a stale close reason shows the failure overlay on a
  // room nobody has tried yet. Leaving to a non-room route keeps the status, so
  // players still get the room closed notice on /join.
  const statusRoomCodeRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (!urlRoomCode || statusRoomCodeRef.current === urlRoomCode) {
      return;
    }
    statusRoomCodeRef.current = urlRoomCode;
    setConnectionState(ConnectionState.DISCONNECTED);
    setCloseReason(undefined);
    setIsConnectionReady(false);
    autoConnectInProgressRef.current = false;
  }, [urlRoomCode]);

  // Connection readiness - wait for WebSocket connection + initial STATE message + handshake completion
  // Hazard: one shared promise per attempt, so a retry can inherit an older
  // attempt's rejection. And a handshake slower than the 3s timeout below
  // rejects for good: isConnectionReady is only set by the auto-connect .then,
  // which does not run again once the state reaches CONNECTED, so the loading
  // overlay sticks on a healthy connection. Derive readiness from client events.
  const waitForConnectionReady = useCallback(() => {
    // Return existing promise if already in progress
    if (connectionReadyPromiseRef.current) {
      logger.debug(
        'waitForConnectionReady already in progress, returning existing promise'
      );
      return connectionReadyPromiseRef.current;
    }

    // Create new promise and store it
    const promise = new Promise<void>((resolve, reject) => {
      const { wsClient } = contextValue;

      // Check if already connected, has received initial state, and handshake is complete
      if (
        wsClient.ws?.readyState === WebSocket.OPEN &&
        connectionState === ConnectionState.CONNECTED
      ) {
        // For already connected state, we still need to ensure we have room state
        // Fall through to the full readiness check including state sync
        logger.debug(
          'WebSocket is connected, proceeding with full readiness check...'
        );
      }

      let openReceived = false;
      let clientInfoComplete = false;

      function maybeResolve() {
        if (openReceived && clientInfoComplete) {
          cleanup();
          // Set connected room ref now that connection is fully ready
          if (urlRoomCode) {
            connectedRoomCodeRef.current = urlRoomCode;
          }
          logger.debug('WebSocket is ready (connection + client info)');
          connectionReadyPromiseRef.current = null; // Clear promise ref
          resolve();
        }
      }

      function onOpen() {
        logger.debug('WebSocket opened');
        openReceived = true;
        maybeResolve();
      }

      function onClientInfoComplete() {
        logger.debug('Client info completed');
        clientInfoComplete = true;
        maybeResolve();
      }

      function onConnectionState(payload: { state: string }) {
        if (payload.state === ConnectionState.CONNECTED) {
          openReceived = true;
          maybeResolve();
        }
      }

      function cleanup() {
        wsClient.off('connectionState', onConnectionState);
        wsClient.off('clientInfoComplete', onClientInfoComplete);
        if (wsClient.ws) wsClient.ws.removeEventListener('open', onOpen);
        if (timeout) clearTimeout(timeout);
      }

      // Set up listeners
      wsClient.on('connectionState', onConnectionState);
      wsClient.on('clientInfoComplete', onClientInfoComplete);

      // Check current state
      if (wsClient.ws?.readyState === WebSocket.OPEN) {
        openReceived = true;
      } else if (wsClient.ws) {
        wsClient.ws.addEventListener('open', onOpen);
      }

      // Check if already connected
      if (connectionState === ConnectionState.CONNECTED) {
        openReceived = true;
        clientInfoComplete = true; // Assume client info is complete if already connected
      }

      // Timeout after 3 seconds
      const timeout = setTimeout(() => {
        cleanup();
        const wsState = wsClient.ws
          ? `WebSocket state: ${wsClient.ws.readyState}`
          : 'No WebSocket created.';
        const connState = `Connection is ${connectionState}.`;
        const url = wsClient.ws
          ? `URL is ${wsClient.ws.url}.`
          : 'No URL available.';
        const readinessState = `Readiness: open=${openReceived}, clientInfo=${clientInfoComplete}`;
        connectionReadyPromiseRef.current = null; // Clear promise ref
        reject(
          new Error(
            `Timeout waiting for connection ready. ${wsState} ${connState} ${url} ${readinessState}`
          )
        );
      }, 3000);

      // Initial check
      maybeResolve();
    });

    // Store and return the promise
    connectionReadyPromiseRef.current = promise;
    return promise;
  }, [contextValue, connectionState, urlRoomCode]);

  // Auto-connect when URL room code changes or connection is lost
  useEffect(() => {
    // Do not connect on a socket that is settled or on its way out.
    // Settled states (including a room the server closed) are final: the
    // connection overlay owns recovery, and retryConnection() resets the state
    // to re-trigger auto-connect. DISCONNECTING is the server's warning that it
    // is about to close, sent just before the close frame. In either case
    // reconnecting now would only wait out waitForConnectionReady on a
    // connection that is not coming back. Neither skip can strand us: the
    // effect above clears settled states on a room change, and the browser
    // always fires onclose after DISCONNECTING, which emits the next state and
    // re-runs this effect.
    const isSettledOrClosing =
      connectionState === ConnectionState.FAILED ||
      connectionState === ConnectionState.RECONNECT_FAILED ||
      connectionState === ConnectionState.TIMEOUT ||
      connectionState === ConnectionState.DISCONNECTING;

    if (
      urlRoomCode &&
      connectionState !== ConnectionState.CONNECTED &&
      !isSettledOrClosing
    ) {
      // Check if auto-connect is already in progress
      if (autoConnectInProgressRef.current) {
        logger.debug('Ignoring auto-connect attempt: already in progress');
        return;
      }

      logger.debug(`Room ${urlRoomCode} detected from URL, auto-connecting...`);
      autoConnectInProgressRef.current = true;

      // Handle connection manager errors
      contextValue.connectionManager
        .connectToRoom(urlRoomCode)
        .catch((error) => {
          logger.error('Connection manager failed:', error);
          autoConnectInProgressRef.current = false;
        });

      // Also wait for connection to be ready
      waitForConnectionReady()
        .then(() => {
          setIsConnectionReady(true);
          autoConnectInProgressRef.current = false; // Clear progress tracking
        })
        .catch((error) => {
          logger.error('Auto-connect failed.', error);
          autoConnectInProgressRef.current = false; // Clear progress tracking on error
          // Note: Removed auto-navigation - let user choose via modal
        });
    } else {
      // Clear auto-connect progress when no room code
      logger.debug(
        `Auto-connect conditions not met: urlRoomCode=${urlRoomCode}, connectionState=${connectionState}`
      );
      autoConnectInProgressRef.current = false;
    }
  }, [
    urlRoomCode,
    connectionState,
    contextValue.connectionManager,
    waitForConnectionReady,
    navigateBasedOnRole,
  ]);

  // Transport operations
  const sendCommand = useCallback(
    (command: PlatformCommand) => {
      contextValue.wsClient.sendCommand(command);
    },
    [contextValue]
  );

  const sendQuery = useCallback(
    (query: PlatformQuery) => {
      contextValue.wsClient.sendQuery(query);
    },
    [contextValue]
  );

  // Registration functions for hierarchical event forwarding
  const registerRoomEventHandler = useCallback(
    (handler: (payload: { events: PlatformEvent[] }) => void) => {
      roomEventCallbackRef.current = handler;
    },
    []
  );

  // Navigation cleanup callback registration
  const registerCleanupCallback = useCallback(
    (callback: () => void) => {
      cleanupCallbacks.add(callback);
      return () => cleanupCallbacks.delete(callback);
    },
    [cleanupCallbacks]
  );

  // Manual retry connection function
  const retryConnection = useCallback(async () => {
    if (!urlRoomCode) {
      logger.warn('No room code available for retry');
      return;
    }

    logger.debug(`Manual retry connection to room ${urlRoomCode}...`);

    // Clear close reason and set to connecting (transitions to loading modal)
    setCloseReason(undefined);
    setConnectionState(ConnectionState.CONNECTING);
    await delay(1000); // Show loading for minimum time so user sees retry happened

    // Force disconnect and reset for retry
    setConnectionState(ConnectionState.DISCONNECTED);
    contextValue.connectionManager.disconnect();
    autoConnectInProgressRef.current = false;
    setIsConnectionReady(false);

    // Auto-connect will trigger and create new failure if it fails again
  }, [urlRoomCode, contextValue.connectionManager]);

  // Clear close reason (for room closed modal dismissal)
  const clearCloseReason = useCallback(() => {
    setCloseReason(undefined);
  }, []);

  const value = useMemo(
    () => ({
      ...contextValue,
      connectionState,
      closeReason,
      isManuallyClosed: contextValue.wsClient.isManuallyClosed,
      isConnectionReady,
      waitForConnectionReady,
      sendCommand,
      sendQuery,
      retryConnection,
      clearCloseReason,
      registerRoomEventHandler,
      registerCleanupCallback,
    }),
    [
      contextValue,
      connectionState,
      closeReason,
      isConnectionReady,
      waitForConnectionReady,
      sendCommand,
      sendQuery,
      retryConnection,
      clearCloseReason,
      registerRoomEventHandler,
      registerCleanupCallback,
    ]
  );

  return (
    <RoomSocketContext.Provider value={value}>
      {children}
    </RoomSocketContext.Provider>
  );
};
